import { useEffect } from 'react'
import { useQuery, useReactiveVar, useSubscription } from '@apollo/client'
import {
  GetUserBlockchainBalancesQuery,
  UserBlockchainBalanceChangedSubscription
} from '__generated__/graphql'
import { GET_USER_BLOCKCHAIN_BALANCES } from 'api/balance/get-user-blockchain-balances'
import { SUBSCRIBE_USER_BLOCKCHAIN_BALANCE } from 'api/balance/subscribe-user-blockchain-balance'
import {
  balanceVar,
  initialUserBlockChainBalance,
  walletBalancesVar
} from 'shared/store/balance-store'
import { globalOverlayBalanceLoadingVar } from 'shared/store/global-overlay-state-store'
import { playWalletAddressVar } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'

// eslint-disable-next-line max-statements
export const useBlockChainBalance = () => {
  const user = useReactiveVar(userVar)
  const playWalletAddress = useReactiveVar(playWalletAddressVar)
  const walletBalances = useReactiveVar(walletBalancesVar)

  const {
    data: userBlockchainBalancesQuery,
    loading: userBlockchainBalancesQueryLoading
  } = useQuery<GetUserBlockchainBalancesQuery>(GET_USER_BLOCKCHAIN_BALANCES, {
    skip: !user?.id
  })

  const {
    data: userBlockchainBalanceSubscription,
    loading: userBlockchainBalanceSubscriptionLoading
  } = useSubscription<UserBlockchainBalanceChangedSubscription>(
    SUBSCRIBE_USER_BLOCKCHAIN_BALANCE,
    { skip: !user?.id }
  )

  useEffect(() => {
    if (!user?.id) {
      walletBalancesVar({})
      return
    }

    const balances = userBlockchainBalancesQuery?.getUserBlockchainBalances
    if (!balances) return

    walletBalancesVar(
      Object.fromEntries(
        balances.map(balance => [balance.walletAddress, balance])
      )
    )
  }, [user?.id, userBlockchainBalancesQuery])

  useEffect(() => {
    const changedBalance =
      userBlockchainBalanceSubscription?.userBlockchainBalanceChanged
    if (!changedBalance) return

    walletBalancesVar({
      ...walletBalancesVar(),
      [changedBalance.walletAddress]: changedBalance
    })
  }, [userBlockchainBalanceSubscription])

  useEffect(() => {
    const playBalance =
      playWalletAddress ? walletBalances[playWalletAddress] : null

    balanceVar(playBalance ?? initialUserBlockChainBalance)
  }, [playWalletAddress, walletBalances])

  useEffect(() => {
    globalOverlayBalanceLoadingVar(
      Boolean(user?.id) && userBlockchainBalancesQueryLoading
    )
  }, [user?.id, userBlockchainBalancesQueryLoading])

  return {
    loading:
      userBlockchainBalancesQueryLoading ||
      userBlockchainBalanceSubscriptionLoading
  }
}
